// Central business data for MRE Developments (M.R.E. Developments Ltd.).
// Real NAP, services and imagery ported from the legacy WordPress sites.

export const NAP = {
  name: "MRE Developments",
  legalName: "M.R.E. Developments Ltd.",
  phoneDisplay: "(587) 899-0101",
  phoneHref: "+15878990101",
  phoneDisplay2: "(587) 896-0101",
  phoneHref2: "+15878960101",
  email: "hello@mredevelopments.com",
  street: "1344, 919 Centre Street N",
  city: "Calgary",
  region: "AB",
  regionName: "Alberta",
  postalCode: "T2E 2P6",
  country: "CA",
  countryName: "Canada",
  geo: { lat: 51.0664, lng: -114.0628 },
  priceRange: "$$",
  areaServed: ["Calgary", "Airdrie", "Cochrane", "Okotoks", "Chestermere", "Calgary Region"],
  hours: "Mo-Fr 08:00-17:00",
  // Where each side of the business actually operates. The Calgary address is
  // the head office, not the limit of who we serve — copy should reflect that
  // so out-of-region visitors don't assume we're Calgary-only.
  serviceArea: {
    // Construction crews work out of Calgary and the surrounding region.
    constructionLabel: "Calgary & area",
    constructionLong: "Calgary and the surrounding region",
    constructionCities: ["Calgary", "Airdrie", "Cochrane", "Okotoks", "Chestermere", "Strathmore", "High River"],
    // Branding, web, marketing and AI are delivered remotely, Canada-wide.
    digitalLabel: "Canada-wide",
    digitalLong: "remotely across Canada",
    // One-line summary used in footers and global copy.
    summary: "Construction across Calgary & area · Branding, web & AI Canada-wide",
  },
  social: {
    instagram: "https://www.instagram.com/mredevelopments/",
    facebook: "https://www.facebook.com/Mredevelopments/",
    youtube: "https://www.youtube.com/@mredevelopments",
  },
};

export const SITE_URL = "https://mredevelopments.com";

// Curated Unsplash photography. Every image is attributed to its photographer
// in IMAGE_CREDITS below and surfaced in a footer credits section.
const U = (id) => `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=1600&q=80`;

export const IMAGES = {
  heroFallback: U("1613490493576-7fde63acd811"),    // Avi Werde
  buildPanel: U("1628012209120-d9db7abf7eab"),       // Felix
  growPanel: U("1526887593587-a307ea5d46b4"),        // Alexandru Acea
  portfolio1: U("1684691376857-5dfb87f6bc65"),       // Diogo Paulos
  blueprintTexture: U("1657346088167-b982455bf29a"), // George Barros
  interior: U("1610177534644-34d881503b83"),         // Jean-Philippe Delberghe
  kitchen: U("1502005097973-6a7082348e28"),          // Jason Briscoe
  commercial: U("1664464683525-29b5d442af54"),       // Coworking Macherzentrum
  brandwork: U("1496664444929-8c75efb9546f"),        // Vadim Sherbakov
  reno_before: "/projects/mre-before.webp?v=wm1",         // Michael's September 19 before photo
  reno_after: "/projects/mre-after.webp?v=wm1",           // Matching after photo supplied by Michael
  team: U("1559136555-9303baea8ebd"),                // Proxyclick
  webdesign: U("1510074377623-8cf13fb86c08"),        // Annie Spratt
  ai: U("1497215842964-222b430dc094"),               // Alesia Kazantceva
  customHome: U("1721815693498-cc28507c0ba2"),       // Naksha Banwao
  exterior: U("1691425700585-c108acad6467"),         // Andre Morales Kalamar
  house: U("1523217582562-09d0def993a6"),            // Pixasquare
  office: U("1720139290958-d8676702c3ed"),           // nrd
  web2: U("1502810190503-8303352d0dd1"),             // Elsa Noblet
  architecture2: U("1598911096723-af003b4ea77a"),    // Arno Senoner
  // Real MRE Developments job-site photography (self-hosted from /public/projects)
  realBathroom: "/projects/bathroom.jpg?v=wm1",
  realBathroom2: "/projects/bathroom-2.jpg?v=wm1",
  realConcrete: "/projects/concrete.jpg?v=wm1",
  realConcrete2: "/projects/concrete-2.jpg?v=wm1",
  realFraming: "/projects/framing.jpg?v=wm1",
  realAdditions: "/projects/additions.jpg?v=wm1",
  realDeck: "/projects/deck-pergola.jpg?v=wm1",
  realElectrical: "/projects/electrical.jpg?v=wm1",
  realFlooring: "/projects/flooring.jpg?v=wm1",
  realFlooring2: "/projects/flooring-2.jpg?v=wm1",
  realDemolition: "/projects/demolition.jpg?v=wm1",
  realEpoxy: "/projects/epoxy.jpg?v=wm1",
  realFullReno: "/projects/full-reno.jpg?v=wm1",
  realFullReno2: "/projects/full-reno-2.jpg?v=wm1",
  realNewHome: "/projects/new-home.jpg?v=wm1",
  realNewHome2: "/projects/new-home-2.jpg?v=wm1",
  realRoofing: "/projects/roofing.jpg?v=wm1",
  realRoofing2: "/projects/roofing-2.jpg?v=wm1",
};

// Photographer attribution for every Unsplash image used on the site.
// Surfaced in the footer "Photography credits" section. Unsplash images are
// free to use; we credit each artist as a courtesy and best practice.
export const IMAGE_CREDITS = [
  { name: "MRE Developments (project photography)", url: "https://www.instagram.com/mredevelopments/" },
  { name: "Avi Werde", url: "https://unsplash.com/@aviwerde" },
  { name: "Felix", url: "https://unsplash.com/@felifox" },
  { name: "Alexandru Acea", url: "https://unsplash.com/@alexacea" },
  { name: "Diogo Paulos", url: "https://unsplash.com/@dpaulos6" },
  { name: "George Barros", url: "https://unsplash.com/@geo_bar" },
  { name: "Jean-Philippe Delberghe", url: "https://unsplash.com/@jipy32" },
  { name: "Jason Briscoe", url: "https://unsplash.com/@jsnbrsc" },
  { name: "Coworking Macherzentrum", url: "https://unsplash.com/@macherzentrum" },
  { name: "Vadim Sherbakov", url: "https://unsplash.com/@madebyvadim" },
  { name: "Bilal Mansuri", url: "https://unsplash.com/@itsbilalmn" },
  { name: "Kam Idris", url: "https://unsplash.com/@ka_idris" },
  { name: "Proxyclick", url: "https://unsplash.com/@proxyclick" },
  { name: "Annie Spratt", url: "https://unsplash.com/@anniespratt" },
  { name: "Alesia Kazantceva", url: "https://unsplash.com/@alesiaskaz" },
  { name: "Naksha Banwao", url: "https://unsplash.com/@nakshabanwao" },
  { name: "Andre Morales Kalamar", url: "https://unsplash.com/@andre___mk" },
  { name: "Pixasquare", url: "https://unsplash.com/@pixasquare" },
  { name: "nrd", url: "https://unsplash.com/@nicotitto" },
  { name: "Elsa Noblet", url: "https://unsplash.com/@elsanoblet" },
  { name: "Arno Senoner", url: "https://unsplash.com/@arnosenoner" },
];

export const STATS = [
  { value: 120, suffix: "+", label: "Projects delivered" },
  { value: 18, suffix: "yrs", label: "Building in Calgary" },
  { value: 2, suffix: "-in-1", label: "Structure + signal" },
  { value: 100, suffix: "%", label: "Licensed & insured" },
];

export const PROCESS = [
  {
    no: "01",
    title: "Free consultation",
    body: "We listen first. Tell us the goal — a home, a space, a brand, growth. No jargon, no pressure, no surprises.",
  },
  {
    no: "02",
    title: "Plan & quote",
    body: "Clear scope, clear timeline, a transparent quote. You see exactly what you get and what it costs.",
  },
  {
    no: "03",
    title: "Build & launch",
    body: "Our crews and designers execute with weekly updates. Construction on site, brand and marketing in parallel.",
  },
  {
    no: "04",
    title: "Handover & grow",
    body: "We hand over a finished build and a live digital presence — then keep optimizing the parts that drive growth.",
  },
];

export const DIGITAL_SERVICES = [
  {
    id: "branding",
    service: "branding",
    title: "Branding & Identity",
    desc: "Names, logos, and brand systems that make a small business look like the leader it's becoming.",
    points: ["Logo & visual identity", "Brand guidelines", "Print & signage"],
  },
  {
    id: "web",
    service: "web-design",
    title: "Web Design & Build",
    desc: "Fast, modern, conversion-focused websites. Like the one you're reading right now.",
    points: ["Custom websites", "E-commerce", "Technical SEO"],
  },
  {
    id: "marketing",
    service: "online-marketing",
    title: "Online Marketing",
    desc: "Search, social and local campaigns that put your business in front of Calgary buyers.",
    points: ["SEO & local search", "Paid ads", "Content & social"],
  },
  {
    id: "ai-data",
    service: "ai-automation",
    title: "AI & Data Services",
    desc: "Practical automation and AI tools — lead concierges, dashboards, and workflows that save hours.",
    points: ["AI assistants & automation", "Analytics dashboards", "Data & integrations"],
  },
];

export const PROJECTS = [
  {
    id: "centre-st-infill",
    service: "new-home-construction",
    title: "New Home Construction",
    discipline: "Construction",
    category: "Custom Home",
    location: "Calgary, AB",
    year: "2026",
    image: IMAGES.realNewHome,
    summary:
      "Ground-up single-family new build — engineered, permitted and delivered on schedule for a Calgary homeowner.",
  },
  {
    id: "full-home-reno",
    service: "home-renovations",
    title: "Full Home Renovation",
    discipline: "Construction",
    category: "Renovation",
    location: "Calgary, AB",
    year: "2026",
    image: IMAGES.realFullReno,
    summary:
      "End-to-end transform — layout redesign, structural updates, plumbing, electrical and new finishes, managed by one team.",
  },
  {
    id: "bathroom-reno",
    service: "bathroom-renovations",
    title: "Bathroom Renovation",
    discipline: "Construction",
    category: "Renovation",
    location: "Calgary, AB",
    year: "2026",
    image: IMAGES.realBathroom,
    summary:
      "A spa-grade ensuite — waterproofed with the Schluter Kerdi system, custom tile and a clean, modern finish.",
  },
  {
    id: "concrete-foundation",
    service: "concrete",
    title: "Concrete & Foundations",
    discipline: "Construction",
    category: "Concrete",
    location: "Calgary, AB",
    year: "2026",
    image: IMAGES.realConcrete,
    summary:
      "Footings, foundations and flatwork poured level, square and built to carry the load for decades.",
  },
  {
    id: "deck-pergola",
    service: "decks-fences",
    title: "Deck & Pergola Build",
    discipline: "Construction",
    category: "Outdoor Living",
    location: "Calgary, AB",
    year: "2025",
    image: IMAGES.realDeck,
    summary:
      "Custom deck and pergola built for Alberta weather — a comfortable, functional extension of the home.",
  },
  {
    id: "roofing-replacement",
    service: "roofing",
    title: "Roof Replacement",
    discipline: "Construction",
    category: "Roofing",
    location: "Calgary, AB",
    year: "2025",
    image: IMAGES.realRoofing,
    summary:
      "A full re-roof completed to spec — durable materials, clean lines and protection that lasts through Calgary winters.",
  },
  {
    id: "brand-studio",
    service: "branding",
    title: "Branding & Identity",
    discipline: "Digital",
    category: "Branding",
    location: "Calgary, AB",
    year: "2026",
    image: IMAGES.brandwork,
    summary:
      "Logo, identity system and brand guidelines built in-house — a cohesive look that makes a business memorable.",
  },
  {
    id: "web-launch",
    service: "web-design",
    title: "Website & Local SEO",
    discipline: "Digital",
    category: "Web + Marketing",
    location: "Calgary, AB",
    year: "2026",
    image: IMAGES.webdesign,
    summary:
      "A fast, mobile-first website with local SEO and lead capture — designed to be found and to convert.",
  },
  {
    id: "ai-concierge",
    service: "ai-automation",
    title: "AI Lead Concierge",
    discipline: "Digital",
    category: "AI + Automation",
    location: "Calgary, AB",
    year: "2026",
    image: IMAGES.ai,
    summary:
      "An automated estimator and lead concierge — like the one on this site — that captures and qualifies leads 24/7.",
  },
];

export const FAQS = [
  {
    q: "What types of projects does MRE Developments take on?",
    a: "We specialize in custom home construction, full renovations, additions, secondary suites and commercial projects across Calgary — plus the full range of trades: concrete, framing, electrical, plumbing, roofing, flooring and more. We also run an in-house design studio for branding, web and marketing.",
  },
  {
    q: "How much does a consultation and quote cost?",
    a: "Nothing. Our initial consultations and quotes are completely free. Contact us to book a consultation and we'll give you a clear, transparent quote based on your specific project.",
  },
  {
    q: "Are you licensed and insured?",
    a: "Yes. M.R.E. Developments Ltd. is fully licensed and insured for residential and commercial construction in Calgary, and we adhere to all relevant building codes and City of Calgary permit requirements.",
  },
  {
    q: "How long does a typical project take?",
    a: "It depends on scope and complexity. During your free consultation we'll walk you through a realistic timeline for your specific project — and keep you updated at every stage so you always know what's next.",
  },
  {
    q: "Can I make changes once construction has begun?",
    a: "We know plans evolve, and we're flexible with reasonable changes. We'll always tell you up front if a change affects the timeline or cost — no surprises, no hidden fees.",
  },
  {
    q: "Do you really do both construction and digital marketing?",
    a: "Yes. We build the physical space your business lives in and the brand, website and marketing that make it grow. Most firms do one. Doing both under one roof is the whole point of MRE.",
  },
];

// Rules-based estimator matrix. Ranges are clearly-marked BALLPARK estimates, not quotes.
export const ESTIMATOR = {
  tracks: [
    { id: "build", label: "Build / Renovate" },
    { id: "grow", label: "Grow (Digital & AI)" },
    { id: "both", label: "Both — Build + Launch" },
  ],
  projectTypes: {
    build: [
      { id: "custom-home", label: "Custom Home", base: [450000, 1200000] },
      { id: "renovation", label: "Renovation", base: [35000, 180000] },
      { id: "commercial", label: "Commercial Build-Out", base: [80000, 450000] },
    ],
    grow: [
      { id: "branding", label: "Branding & Identity", base: [3500, 14000] },
      { id: "website", label: "Website Design & Build", base: [4000, 22000] },
      { id: "marketing", label: "Marketing Campaign", base: [1500, 6000] },
      { id: "ai", label: "AI & Automation", base: [3000, 20000] },
    ],
    both: [
      { id: "space-launch", label: "Space + Brand Launch", base: [90000, 500000] },
    ],
  },
  scopes: [
    { id: "small", label: "Focused / Small", mult: 0.7 },
    { id: "medium", label: "Standard / Medium", mult: 1.0 },
    { id: "large", label: "Ambitious / Large", mult: 1.5 },
  ],
  timelines: [
    { id: "flexible", label: "Flexible" },
    { id: "3-6m", label: "3–6 months" },
    { id: "asap", label: "ASAP / Rush", mult: 1.12 },
  ],
};

